import { motion } from "framer-motion";
import { Check, Building2, Rocket, Factory, Home, Zap, Globe, AlertTriangle, GitMerge } from "lucide-react";

const SERVICES = [
  "Equity Funding & Investor Connects",
  "Debt Funding & Working Capital",
  "Project Finance",
  "Loan Against Property / Shares",
  "IPO & Growth Advisory",
  "Stressed Company Trading & Restructuring",
  "Market Expansion & Strategic Consulting",
  "FMCG, Engineering & Industrial Business Support",
];

const FUNDING_FOR = [
  { icon: Building2, label: "MSME Funding" },
  { icon: Rocket, label: "Startup Funding" },
  { icon: Factory, label: "Manufacturing Units" },
  { icon: Home, label: "Real Estate Projects" },
  { icon: Zap, label: "Infrastructure & Energy" },
  { icon: Globe, label: "Export / Import Businesses" },
  { icon: AlertTriangle, label: "Distressed Asset Acquisition" },
  { icon: GitMerge, label: "Bridge Finance & Structured Deals" },
];

export default function CapitalFunding() {
  return (
    <section
      id="capital-funding"
      data-testid="capital-funding-section"
      className="relative bg-[#0A1F44] text-white py-24 md:py-32 cgp-grain overflow-hidden"
    >
      {/* Decorative gold lines */}
      <div className="absolute top-0 left-0 right-0 cgp-divider" />
      <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#C8A24A]/5 blur-3xl pointer-events-none" />

      <div className="cgp-container relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="cgp-overline">Division 01 — Capital Funding</span>
          <h2 className="font-serif font-light text-white mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Customised financial solutions
            <br />
            <span className="italic text-white/65">for every business need.</span>
          </h2>
          <p className="mt-7 text-white/70 text-lg leading-relaxed max-w-2xl">
            Your trusted partner for financial solutions and business growth.
            We bridge ambition and capital with a network of banks, NBFCs,
            family offices and institutional investors.
          </p>
        </motion.div>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Our Services */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            data-testid="our-services-card"
            className="bg-[#051024]/60 border border-white/10 backdrop-blur-sm p-8 md:p-10"
          >
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-serif text-2xl md:text-3xl text-white">
                Our Services
              </h3>
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#C8A24A]">
                Advisory
              </span>
            </div>
            <span className="cgp-gold-rule" />
            <ul className="mt-8 space-y-5">
              {SERVICES.map((s, idx) => (
                <motion.li
                  key={s}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.04 }}
                  data-testid={`service-item-${idx}`}
                  className="flex items-start gap-3 text-white/85"
                >
                  <Check size={18} className="cgp-check" />
                  <span className="leading-relaxed">{s}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Funding For */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="funding-for-card"
            className="bg-[#051024]/60 border border-white/10 backdrop-blur-sm p-8 md:p-10"
          >
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-serif text-2xl md:text-3xl text-white">
                Funding For
              </h3>
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#C8A24A]">
                Sectors
              </span>
            </div>
            <span className="cgp-gold-rule" />
            <ul className="mt-8 grid grid-cols-1 gap-4">
              {FUNDING_FOR.map((f, idx) => (
                <motion.li
                  key={f.label}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.04 }}
                  data-testid={`funding-for-item-${idx}`}
                  className="flex items-center gap-4 text-white/85 py-3 border-b border-white/5 last:border-b-0"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#C8A24A]/10 border border-[#C8A24A]/30 text-[#C8A24A]">
                    <f.icon size={16} strokeWidth={1.6} />
                  </span>
                  <span>{f.label}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Bottom strap */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-[#C8A24A] uppercase tracking-[0.32em] text-xs">
            Strategic Funding · Smart Solutions · Sustainable Growth
          </p>
        </motion.div>
      </div>
    </section>
  );
}
